/**
 * The draft card's session list (boards Q, R), read off the plan DOCUMENT rather than the wire.
 *
 * The session's panel is the builder's own day form, which counts through `entities/plan-draft`'s
 * `dayCard`: main-work exercises, and every set but the warm-up. The card's rows go through the
 * same projection, so „5 ćwiczeń · 14 serii" on the row is what the panel's head says when the row
 * is pressed. The length is the session's AUTHORED one; nothing estimates a time from set counts
 * (execution ADR §1), so a draft that states none shows none.
 *
 * One list per phase once the plan has two, each under its name and its cycles.
 *
 * Pure: the document and the build result in, rows out.
 */

import type { AiBuildResult } from '~/entities/ai-conversation';
import { dayCard, type DayDraft, type PlanDraft } from '~/entities/plan-draft';

/** One session as the draft card lists it. The card's row type, stated here in the card's shape. */
export interface DraftSessionRow {
  readonly daySlotToken: string;
  /** The letter the session wears on the card and in its panel: A for a phase's first, and on. */
  readonly letter: string;
  readonly name: string | null;
  readonly exercises: number;
  readonly sets: number;
  readonly minutes: number | null;
  readonly missing: number;
}

export interface DraftSessionGroup {
  readonly phase: { readonly name: string; readonly fromCycle: number; readonly toCycle: number } | null;
  readonly rows: readonly DraftSessionRow[];
}

/** A session the panel can step to and move an exercise to, in the shape the day form takes. */
export interface SessionMoveTarget {
  readonly slotToken: string;
  readonly weekday: null;
  readonly label: string;
  readonly shortLabel: string;
  readonly blockCount: number;
}

/** The letter a session wears: A for a phase's first, and on. */
export function sessionLetter(index: number): string {
  return String.fromCharCode(65 + (index % 26));
}

/**
 * The card's sessions: phase by phase under each phase's name and cycles once the plan has more
 * than one, and as one unlabelled list when it has one.
 *
 * Every phase carries its own days, and an edit in a session's panel reaches that phase's day
 * alone. A list drawn once for phases that merely READ alike would open the first phase's day for
 * all of them, and the trainer would believe an edit reached cycles it never touched; so a second
 * phase is always its own group.
 */
export function draftSessionGroups(draft: PlanDraft, result: AiBuildResult): DraftSessionGroup[] {
  // The slots of every name still waiting for a decision: what a session counts as missing.
  const undecided = new Set(
    result.unresolved.filter((exercise) => exercise.decision === null).flatMap((exercise) => exercise.slotTokens),
  );
  const isPhased = draft.phases.length > 1;
  return draft.phases.map((phase): DraftSessionGroup => ({
    phase: isPhased
      ? { name: phase.name, fromCycle: phase.startCycle, toCycle: phase.startCycle + phase.cycles - 1 }
      : null,
    rows: phase.days.map((day, index) => {
      const card = dayCard(day);
      return {
        daySlotToken: day.slotToken,
        letter: sessionLetter(index),
        name: card.title,
        exercises: card.mainExerciseCount,
        sets: card.countingSetCount,
        minutes: card.durationMinutes,
        missing: day.exercises.filter((exercise) => undecided.has(exercise.slotToken)).length,
      };
    }),
  }));
}

/**
 * The sessions the panel can step to and move an exercise to: the days of the phase the open
 * session stands in, each named by its letter and its session.
 *
 * The builder's panel takes this list from its canvas, which knows the coordinates; here the
 * coordinates are the card's letters. No weekday: there is no week grid behind this panel for a
 * drop to land on, so a cross-session move is the ⋯ menu's.
 */
export function sessionMoveTargets(
  days: readonly DayDraft[],
  words: {
    readonly session: (letter: string) => string;
    /** The target's full name from its short name and its session's title. */
    readonly label: (short: string, title: string) => string;
    readonly untitled: string;
  },
): SessionMoveTarget[] {
  return days.map((day, index) => {
    const card = dayCard(day);
    const short = words.session(sessionLetter(index));
    return {
      slotToken: day.slotToken,
      weekday: null,
      label: words.label(short, card.title ?? words.untitled),
      shortLabel: short,
      blockCount: card.blocks.length,
    };
  });
}
